import { useCallback, useEffect, useState } from 'react'

import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { Banner, BannerVariants, Icon, Image, Text, Toggle } from '@/ui'
import { TypographyStyles, TextColors, mtc } from '@/styles'
import { OnboardingStickyNav } from '@/features/onboarding/shared/OnboardingStickyNav'
import { useFixedFooterOffset } from '@/features/onboarding/shared/useFixedFooterOffset'
import { institutionLogoMap } from '@/constants/logoConstants'
import { faBank } from '@fortawesome/pro-regular-svg-icons'
import { useAccountTypeAnimation } from './useAccountTypeAnimation'
import { BankQuestionIcon } from './BankQuestionIcon'
import { ALL_ACCOUNTS, INSTITUTIONS } from './accountType.fixtures'

interface AccountTypeProps {
  onBack?: () => void
  onContinue?: () => void
}

type PrimaryUse = 'business' | 'personal'
type DerivedAccountType =
  | 'business_only'
  | 'business_mixed'
  | 'personal_mixed'
  | 'personal_only'

const PRIMARY_USE_OPTIONS: { value: PrimaryUse; label: string }[] = [
  { value: 'business', label: 'Business' },
  { value: 'personal', label: 'Personal' },
]

const YES_NO_OPTIONS: { value: boolean; label: string }[] = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

interface AccountAnswer {
  primaryUse: PrimaryUse | null
  hasOtherType: boolean | null
}

const getAccountType = (answer: AccountAnswer): DerivedAccountType | null => {
  if (!answer.primaryUse) return null
  if (answer.primaryUse === 'business') {
    return answer.hasOtherType ? 'business_mixed' : 'business_only'
  }
  return answer.hasOtherType ? 'personal_mixed' : 'personal_only'
}

// Shown once Layer 2 is answered, the consistent promise: you don't have
// to find these transactions yourself, we'll sort it.
const FINAL_MESSAGES: Record<DerivedAccountType, string> = {
  business_only:
    "We'll categorize everything here as business. If anything personal slips in, we'll catch it.",
  business_mixed:
    "We'll categorize charges here as business and pull out the personal ones for you, so you don't have to find them yourself.",
  personal_mixed:
    "We'll keep this out of your books but grab the business charges, so you don't miss any deductions.",
  personal_only:
    "We'll keep this out of your books, but we'll still scan it for any business charges so you don't miss a deduction.",
}

export const AccountType = ({ onBack, onContinue }: AccountTypeProps) => {
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const leftOffset = useFixedFooterOffset()
  const { accountListRef, questionMarkRef } = useAccountTypeAnimation()

  const [answers, setAnswers] = useState<Record<string, AccountAnswer>>(() =>
    Object.fromEntries(
      ALL_ACCOUNTS.map((account) => [
        account.id,
        { primaryUse: null, hasOtherType: null },
      ])
    )
  )
  const [showValidationError, setShowValidationError] = useState(false)

  useEffect(() => {
    pageView('onboarding account type')
  }, [pageView])

  const handleSelectPrimaryUse = useCallback(
    (accountId: string, primaryUse: PrimaryUse) => {
      track('onboarding_account_type_select_primary_use', {
        accountId,
        primaryUse,
      })
      setAnswers((prev) => ({
        ...prev,
        [accountId]: {
          primaryUse,
          // Personal skips the follow-up entirely, personal-with-some-
          // business is an open connection-behavior question (see handoff
          // doc), so every Personal selection is personal_only for now.
          // Business still needs the follow-up since it determines whether
          // personal charges need separating out.
          hasOtherType: primaryUse === 'personal' ? false : null,
        },
      }))
      setShowValidationError(false)
    },
    [track]
  )

  const handleSelectHasOtherType = useCallback(
    (accountId: string, hasOtherType: boolean) => {
      track('onboarding_account_type_select_has_other_type', {
        accountId,
        hasOtherType,
      })
      setAnswers((prev) => ({
        ...prev,
        [accountId]: { ...prev[accountId], hasOtherType },
      }))
      setShowValidationError(false)
    },
    [track]
  )

  const handleBack = useCallback(() => {
    track('onboarding_account_type_back')
    onBack?.()
  }, [track, onBack])

  const handleContinue = useCallback(() => {
    const hasUnresolvedAccounts = ALL_ACCOUNTS.some((account) => {
      const answer = answers[account.id]
      return !answer?.primaryUse || answer.hasOtherType === null
    })

    if (hasUnresolvedAccounts) {
      track('onboarding_account_type_continue_blocked')
      setShowValidationError(true)
      return
    }

    const accountTypes = ALL_ACCOUNTS.map(
      (account) => `${account.id}:${getAccountType(answers[account.id])}`
    )
    track('onboarding_account_type_continue', { accountTypes })
    onContinue?.()
  }, [track, answers, onContinue])

  return (
    <div className="flex min-h-screen w-full flex-col">
      <div className="flex w-full flex-1 flex-col items-center gap-6 px-6 pt-2 pb-30 max-sm:pb-6">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center">
          <BankQuestionIcon
            questionMarkRef={questionMarkRef}
            className="h-20 w-20 object-contain"
          />
        </div>

        <div className="flex max-w-112 flex-col items-center justify-center gap-1 text-center">
          <Text variant={TypographyStyles.DISPLAY_SM} textAlign="center">
            What do you mainly use each account for?
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            textAlign="center"
            className="text-center"
          >
            Most accounts have a little of both, so just pick the main use and
            we&apos;ll sort the rest. You can change this later.
          </Text>
        </div>

        <div
          className="flex w-full max-w-150 flex-col gap-4"
          ref={accountListRef}
        >
          {INSTITUTIONS.map((institution) => (
            <div
              key={institution.id}
              className="flex w-full flex-col items-start gap-4 rounded-3xl border border-neutral-200 bg-neutral-0 p-6 shadow-surface-card"
            >
              <div className="flex w-full items-center gap-4">
                {institutionLogoMap[institution.name] ? (
                  <Image
                    src={institutionLogoMap[institution.name]}
                    alt=""
                    objectFit="contain"
                    className="h-10 w-10 shrink-0 rounded-lg border border-neutral-100 bg-neutral-0 p-1.5"
                  />
                ) : (
                  <div
                    className={mtc(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                      institution.iconColor === 'loud'
                        ? 'bg-green-100'
                        : 'bg-neutral-900'
                    )}
                  >
                    <Icon icon={faBank} color="text-neutral-0" size="lg" />
                  </div>
                )}
                <Text
                  variant={TypographyStyles.LABEL_MD}
                  color={TextColors.HEADER}
                >
                  {institution.name}
                </Text>
              </div>

              {institution.accounts.map((account) => {
                const answer = answers[account.id]

                const accountType = answer ? getAccountType(answer) : null

                return (
                  <div
                    key={account.id}
                    className="flex w-full flex-col gap-2 border-t border-neutral-100 pt-3 first:border-t-0 first:pt-0"
                  >
                    <div className="flex w-full items-center justify-between gap-4">
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <Text
                          variant={TypographyStyles.BODY_SM}
                          color={TextColors.HEADER}
                        >
                          {account.name}
                        </Text>
                        <Text
                          variant={TypographyStyles.BODY_XS}
                          color={TextColors.MUTED}
                        >
                          •••• {account.mask}
                        </Text>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        {PRIMARY_USE_OPTIONS.map((option) => (
                          <Toggle
                            key={option.value}
                            variant="outline"
                            size="lg"
                            checked={answer?.primaryUse === option.value}
                            onCheckedChange={() =>
                              handleSelectPrimaryUse(account.id, option.value)
                            }
                            className="whitespace-nowrap"
                          >
                            {option.label}
                          </Toggle>
                        ))}
                      </div>
                    </div>

                    {showValidationError && !answer?.primaryUse && (
                      <Text
                        variant={TypographyStyles.BODY_XS}
                        color={TextColors.DANGER}
                      >
                        Please select an option before continuing.
                      </Text>
                    )}

                    {answer?.primaryUse === 'business' && (
                      <div className="flex w-full flex-col gap-2 pt-1">
                        <Text
                          variant={TypographyStyles.LABEL_XS}
                          color={TextColors.MUTED}
                        >
                          Any personal charges on it too?
                        </Text>
                        <div className="flex gap-2">
                          {YES_NO_OPTIONS.map((option) => (
                            <Toggle
                              key={String(option.value)}
                              variant="outline"
                              size="sm"
                              checked={answer.hasOtherType === option.value}
                              onCheckedChange={() =>
                                handleSelectHasOtherType(
                                  account.id,
                                  option.value
                                )
                              }
                              className="whitespace-nowrap"
                            >
                              {option.label}
                            </Toggle>
                          ))}
                        </div>
                        {showValidationError &&
                          answer.hasOtherType === null && (
                            <Text
                              variant={TypographyStyles.BODY_XS}
                              color={TextColors.DANGER}
                            >
                              Please answer before continuing.
                            </Text>
                          )}
                      </div>
                    )}

                    {accountType && answer?.hasOtherType !== null && (
                      <Banner
                        variant={BannerVariants.DEFAULT}
                        wrapperClassName="w-full justify-start"
                      >
                        {FINAL_MESSAGES[accountType]}
                      </Banner>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      <OnboardingStickyNav
        leftOffset={leftOffset}
        className="mx-auto max-w-[1200px]"
        onBack={handleBack}
        onContinue={handleContinue}
      />
    </div>
  )
}

export default AccountType
